import { useEffect, useState } from "react";
import LegalLayout, { Section } from "./LegalLayout";
import { Cookie, Check, X } from "lucide-react";

const STORAGE_KEY = "tbts_cookie_consent";

export default function Cookies() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    try {
      setConsent(localStorage.getItem(STORAGE_KEY));
    } catch {
      /* ignore */
    }
  }, []);

  const changeConsent = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
      setConsent(value);
      if (value === "denied" && window.posthog) {
        window.posthog.opt_out_capturing();
        if (typeof window.posthog.stopSessionRecording === "function") {
          window.posthog.stopSessionRecording();
        }
      }
      if (value === "granted" && window.posthog) {
        window.posthog.opt_in_capturing();
        if (typeof window.posthog.startSessionRecording === "function") {
          window.posthog.startSessionRecording();
        }
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <LegalLayout title="Polityka cookies" lastUpdated="28 maja 2026">
      <p className="text-base">
        Serwis telecombts.pl wykorzystuje pliki cookie (tzw. „ciasteczka") w celu zapewnienia prawidłowego
        działania strony oraz — za Twoją zgodą — analizy ruchu i doskonalenia jakości usług.
      </p>

      <div className="not-prose bg-white rounded-2xl border border-slate-200 p-6 my-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5 text-emerald-700" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 mb-1">Twoje aktualne ustawienia</h2>
            <p className="text-sm text-slate-600">
              {consent === "granted" && "Akceptujesz wszystkie pliki cookie (w tym analityczne)."}
              {consent === "denied" && "Zaakceptowałeś wyłącznie niezbędne pliki cookie."}
              {!consent && "Nie podjęto jeszcze decyzji."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => changeConsent("denied")}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
            Tylko niezbędne
          </button>
          <button
            type="button"
            onClick={() => changeConsent("granted")}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <Check className="w-3.5 h-3.5" aria-hidden="true" />
            Akceptuję wszystkie
          </button>
        </div>
      </div>

      <Section title="1. Czym są pliki cookie?">
        <p>
          Pliki cookie to niewielkie pliki tekstowe zapisywane przez przeglądarkę na Twoim urządzeniu.
          Mogą zawierać informacje takie jak preferencje użytkownika, identyfikator sesji czy dane służące
          do analizy ruchu.
        </p>
      </Section>

      <Section title="2. Jakie cookie wykorzystujemy?">
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h3 className="font-bold text-slate-900 text-sm mb-1">Niezbędne (zawsze aktywne)</h3>
            <p className="text-sm text-slate-600">
              Konieczne do prawidłowego działania serwisu — m.in. zapamiętanie Twojej decyzji w sprawie cookie
              oraz obsługa formularza. Tych plików nie można wyłączyć.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Przykład: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200">tbts_cookie_consent</code> (localStorage, przechowywane lokalnie w Twojej przeglądarce).
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h3 className="font-bold text-slate-900 text-sm mb-1">Analityczne (opcjonalne — wymagana zgoda)</h3>
            <p className="text-sm text-slate-600">
              Pomagają nam zrozumieć, jak użytkownicy korzystają z serwisu, abyśmy mogli go ulepszać. Zbieramy
              anonimowe dane o liczbie odwiedzin, najczęściej oglądanych sekcjach i błędach technicznych.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Dostawca: PostHog. Dane przetwarzane wyłącznie po wyrażeniu zgody w bannerze cookie.
            </p>
          </div>
        </div>
      </Section>

      <Section title="3. Jak zarządzać cookie?">
        <p>Twoją zgodę na pliki analityczne możesz w każdej chwili:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>zmienić przyciskami powyżej,</li>
          <li>cofnąć w ustawieniach prywatności przeglądarki (usunięcie plików cookie przez przeglądarkę spowoduje ponowne wyświetlenie bannera),</li>
          <li>całkowicie zablokować w ustawieniach przeglądarki.</li>
        </ul>
        <p className="text-sm text-slate-600 mt-3">
          Instrukcje zarządzania cookie w najpopularniejszych przeglądarkach:
          <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline ml-1">Chrome</a> ·
          <a href="https://support.mozilla.org/pl/kb/W%C5%82%C4%85czanie%20i%20wy%C5%82%C4%85czanie%20obs%C5%82ugi%20ciasteczek" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline ml-1">Firefox</a> ·
          <a href="https://support.apple.com/pl-pl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline ml-1">Safari</a> ·
          <a href="https://support.microsoft.com/pl-pl/windows/usuwanie-plik%C3%B3w-cookie-i-zarz%C4%85dzanie-nimi-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline ml-1">Edge</a>.
        </p>
      </Section>

      <Section title="4. Skutki wyłączenia cookie">
        <p>
          Wyłączenie cookie niezbędnych może spowodować, że niektóre funkcje serwisu (np. zapamiętanie Twojej
          decyzji o cookie) przestaną działać poprawnie. Wyłączenie cookie analitycznych nie wpływa na działanie
          formularza kontaktowego ani na żadną inną funkcję istotną dla użytkownika.
        </p>
      </Section>
    </LegalLayout>
  );
}
